import { useOrganizations } from 'store/organizations';
import { submitServerChange } from 'components/dataCreationForms/common';
import { getClientAdminPermission } from 'shared/domain/permissions/getClientAdminPermission';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useCurrentUser } from 'store/user';
import { useNavigate } from 'react-router-dom';
import { getProjectStoreState, useProjectOrganizationId } from 'store/project';
import { getProjectsStoreState } from 'store/projects';
import { projectCreateOnViewToProjectCreateModel } from 'shared/domain/project/mapping/toModel';
import { projectClient } from 'shared/domain/project/projectClient';
import { ProjectCreateOnView } from 'shared/domain/project/types/view';
import { CreateProjectContext } from '../types';
import {
  handleProjectCreateFormValidation,
  validateProjectCreate,
} from '../validation';
import { createEmptyProject } from './createEmptyProject';
import { useAppNavigation } from 'contexts/navigation';

export const formName = 'project';
const WithCreateProjectContext = createContext<
  CreateProjectContext | undefined
>(undefined);

function WithCreateProject({
  children,
}: PropsWithChildren<{}>): ReactElement {
  const navigate = useNavigate();
  const intl = useIntl();
  const organizationId = useProjectOrganizationId();
  const { organizationsMap, loading: organizationsLoading } =
    useOrganizations();
  const currentOrg = useMemo(() => {
    if (!organizationsLoading) {
      return organizationId
        ? organizationsMap.get(organizationId)
        : undefined;
    }
  }, [organizationId, organizationsLoading, organizationsMap]);

  const { basePermissions } = useCurrentUser();

  const adminPermissionsMap = useMemo(() => {
    return getClientAdminPermission(basePermissions, organizationsMap);
  }, [basePermissions, organizationsMap]);
  const canCreateOnCurrentOrg = useMemo(
    () =>
      currentOrg &&
      adminPermissionsMap &&
      adminPermissionsMap.has(organizationId),
    [adminPermissionsMap, currentOrg, organizationId]
  );

  const initialOrganization = useMemo(() => {
    if (canCreateOnCurrentOrg) {
      return currentOrg;
    }

    const firstKey = adminPermissionsMap?.keys().next().value;
    return firstKey ? organizationsMap.get(firstKey) : undefined;
  }, [
    organizationsMap,
    adminPermissionsMap,
    canCreateOnCurrentOrg,
    currentOrg,
  ]);

  const initialValues: ProjectCreateOnView | undefined =
    initialOrganization && createEmptyProject(intl, initialOrganization);

  const [open, setOpen] = useState(false);
  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);
  const confirmCancel = useCancelConfirmation(formName);
  const closeDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);
  const [isPosting, setIsPosting] = useState(false);
  const { goToProjectDetails } = useAppNavigation();
  const submitForm = useCallback(
    async (values: ProjectCreateOnView) => {
      const outcome = await submitServerChange({
        change: () => projectClient.create(projectCreateOnViewToProjectCreateModel(values)),
        setOpen,
        setIsPosting,
        action: 'creating a project',
      });
      if (!outcome.saved) {
        return;
      }

      const project = outcome.result;
      getProjectsStoreState().actions.reloadShowingLoading();
      void getProjectStoreState().actions.selectProject(project._id);
      goToProjectDetails();
      return project;
    },
    [goToProjectDetails]
  );
  const SUBMIT_EVENT_TYPE = 'submit-project';
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const ctx: CreateProjectContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateProjectCreate,
      handleFormValidation: handleProjectCreateFormValidation,
      formName,
    };
  }, [
    closeDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
  ]);

  return (
    <WithCreateProjectContext.Provider value={ctx}>
      {children}
    </WithCreateProjectContext.Provider>
  );
}

function useCreateProject(): CreateProjectContext {
  const context = useContext(WithCreateProjectContext);
  if (context === undefined) {
    throw new Error(
      'useCreateProject must be used within an CreateProjectContext'
    );
  }
  return context;
}

const withCreateProject =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateProject>
      <Component {...props} />
    </WithCreateProject>
  );

export { WithCreateProject, useCreateProject, withCreateProject };
